document.addEventListener('DOMContentLoaded', function () {
  'use strict';

  const autocompleteRoots = Array.from(document.querySelectorAll('[data-autocomplete-root]'));
  if (autocompleteRoots.length === 0) return;

  autocompleteRoots.forEach((root) => setupAutocomplete(root));

  function setupAutocomplete(root) {
    const inputElement = root.querySelector('[data-autocomplete-input]');
    const resultsContainer = root.querySelector('[data-autocomplete-results]');
    if (!inputElement || !resultsContainer) return;

    let debounceTimeout;
    let loadingTimeout;
    let counter = 0;
    let resultsCache = {};
    let cacheOrder = [];
    let recentTrackedClicks = {};

    const MAX_CACHE_SIZE = 50;
    const MIN_QUERY_LENGTH = 2;
    const DESKTOP_MAX_RESULTS_PER_SECTION = 4;
    const MOBILE_MAX_RESULTS_PER_SECTION = 3;
    const MOBILE_BREAKPOINT = 640;
    const DEBOUNCE_MS = 220;
    const LOADING_DELAY_MS = 180;
    const TRACK_DEDUPE_MS = 10 * 1000;
    const searchClickUrl = inputElement.getAttribute('data-click-url');
    const inNavBar = root.dataset.autocompleteRoot === 'navbar';
    const sectionHeaders = { insights: 'Metric insights', filers: 'Filers', securities: 'Securities' };
    const viewportTarget = window.visualViewport || window;

    inputElement.setAttribute('tabindex', '1');

    inputElement.addEventListener('input', () => {
      counter += 1;
      const localCounter = counter;
      clearTimeout(debounceTimeout);
      debounceTimeout = setTimeout(() => populateAutocomplete(localCounter), DEBOUNCE_MS);
    });

    inputElement.addEventListener('focus', syncDropdownViewport);

    root.addEventListener('keydown', function (e) {
      if ([13, 38, 40].indexOf(e.keyCode) === -1) return;
      if (!autocompleteHasFocus()) return;

      const links = Array.from(resultsContainer.querySelectorAll('.autocomplete-link'));
      if (links.length === 0) return;

      if (e.keyCode === 13) {
        const activeLink = document.activeElement;
        if (activeLink && activeLink.classList.contains('autocomplete-link')) {
          activeLink.click();
        }
        return;
      }

      let currentIndex = links.indexOf(document.activeElement);
      const increment = e.keyCode === 40 ? 1 : -1;
      currentIndex = Math.min(Math.max(currentIndex + increment, -1), links.length - 1);

      if (currentIndex === -1) {
        inputElement.focus();
      } else {
        links[currentIndex].focus();
        e.preventDefault();
      }
    });

    root.addEventListener('click', function (e) {
      const link = e.target.closest('a[data-search-track="filer"]');
      if (!link || !resultsContainer.contains(link)) return;

      trackSearchClick({
        filerId: link.dataset.filerId,
        source: link.dataset.searchSource,
        query: link.dataset.searchQuery
      });
    });

    root.addEventListener('focusin', function (e) {
      const activeLink = e.target.closest('.autocomplete-link');
      if (!activeLink || !resultsContainer.contains(activeLink)) return;

      resultsContainer.querySelectorAll('.autocomplete-link.is-active').forEach((node) => node.classList.remove('is-active'));
      activeLink.classList.add('is-active');
    });

    document.addEventListener('click', function (e) {
      if (root.contains(e.target)) return;
      clearAutocompleteResults();
    });

    viewportTarget.addEventListener('resize', syncDropdownViewport);
    if (window.visualViewport) window.visualViewport.addEventListener('scroll', syncDropdownViewport);
    window.addEventListener('scroll', syncDropdownViewport, { passive: true });
    window.addEventListener('orientationchange', syncDropdownViewport);

    function autocompleteHasFocus() {
      const activeElement = document.activeElement;
      return activeElement === inputElement || (activeElement && resultsContainer.contains(activeElement));
    }

    function populateAutocomplete(lastCounter) {
      if (counter !== lastCounter) return;

      const query = inputElement.value.trim();
      const queryKey = query.toLowerCase();

      if (!query || query.length < MIN_QUERY_LENGTH) {
        clearAutocompleteResults();
        return;
      }

      if (resultsCache[queryKey]) {
        touchCacheKey(queryKey);
        renderResults(resultsCache[queryKey]);
        return;
      }

      const url = `${inputElement.getAttribute('data-url')}?q=${encodeURIComponent(query)}`;

      clearTimeout(loadingTimeout);
      loadingTimeout = setTimeout(() => {
        if (counter !== lastCounter) return;
        if (resultsContainer.innerHTML.trim() !== '') return;

        renderResults(wrapDropdown(`
          <div class="lp-autocomplete-state">Searching...</div>
        `));
      }, LOADING_DELAY_MS);

      fetch(url).then((r) => {
        clearTimeout(loadingTimeout);
        if (!r.ok) throw new Error(`HTTP ${r.status}: ${r.statusText}`);
        return r.json();
      }).then((data) => {
        clearTimeout(loadingTimeout);
        if (counter !== lastCounter) return;

        const sections = ['insights', 'filers', 'securities'].map((key) => {
          const rows = Array.isArray(data[key]) ? data[key].slice(0, maxResultsPerSection()) : [];
          return buildSectionHtml(key, rows, query);
        }).filter(Boolean);

        const body = sections.length > 0 ? sections.join('') : '<div class="lp-autocomplete-state">No results found</div>';
        const html = wrapDropdown(body);

        resultsCache[queryKey] = html;
        cacheOrder.push(queryKey);
        pruneCache();

        renderResults(html);
      }).catch((error) => {
        clearTimeout(loadingTimeout);
        if (counter !== lastCounter) return;

        console.error('Autocomplete error:', error);
        renderResults(wrapDropdown(`
          <div class="lp-autocomplete-state lp-autocomplete-state-error">Search failed. Please try again.</div>
        `));
      });
    }

    function maxResultsPerSection() {
      return window.innerWidth < MOBILE_BREAKPOINT ? MOBILE_MAX_RESULTS_PER_SECTION : DESKTOP_MAX_RESULTS_PER_SECTION;
    }

    function buildSectionHtml(sectionKey, rows, query) {
      if (!rows || rows.length === 0) return '';

      const sectionName = sectionHeaders[sectionKey] || sectionKey;
      const rowsHtml = rows.map((row) => buildRowHtml(row, query)).join('');

      return `
        <section class="lp-autocomplete-section" aria-label="${escapeHtmlAttr(sectionName)}">
          <h3 class="lp-autocomplete-heading">${sectionName}</h3>
          <div class="lp-autocomplete-rows">
            ${rowsHtml}
          </div>
        </section>
      `;
    }

    function buildRowHtml(row, query) {
      const type = ['insight', 'security'].includes(row.type) ? row.type : 'filer';
      const fallbackBadgeLabel = type === 'insight' ? 'Insight' : (type === 'security' ? 'Security' : 'Filer');
      const fallbackReasonText = type === 'insight'
        ? 'Opens a curated SEC-backed metric ranking.'
        : (type === 'security'
            ? 'Opens CUSIP holder trends and quarter pages.'
            : 'Opens filing timeline and ownership profile.');
      const fallbackCtaText = type === 'insight' ? 'Open ranking' : (type === 'security' ? 'Open security' : 'Open filer');
      const badgeLabel = row.badge || fallbackBadgeLabel;
      const reasonText = row.reason || fallbackReasonText;
      const ctaText = row.cta || fallbackCtaText;
      const filerTrackingAttrs = type === 'filer' ? [
        'data-search-track="filer"',
        `data-search-source="${escapeHtmlAttr(row.source || 'autocomplete')}"`,
        `data-filer-id="${escapeHtmlAttr(row.id || '')}"`,
        `data-search-query="${escapeHtmlAttr(row.q || query)}"`
      ].join(' ') : '';
      const insightTrackingAttrs = type === 'insight' ? [
        'data-telemetry-event="lp_search_insight_result_click"',
        'data-telemetry-module="autocomplete_metric_insights"',
        `data-telemetry-cta-label="${escapeHtmlAttr(row.name || 'Metric insight')}"`,
        'data-telemetry-source-label="Autocomplete metric insight"',
        `data-telemetry-search-source="${escapeHtmlAttr(row.source || 'autocomplete')}"`,
        `data-telemetry-search-query-length="${String((row.q || query || '').trim().length)}"`
      ].join(' ') : '';

      return `
        <a href="${escapeHtmlAttr(row.url || '#')}" class="autocomplete-link" tabindex="0" ${filerTrackingAttrs} ${insightTrackingAttrs}>
          <div class="lp-autocomplete-row">
            <div class="lp-autocomplete-row-main">
              <div class="lp-autocomplete-title-line">
                <span class="lp-autocomplete-title">${highlightMatch(row.name || '', query)}</span>
                <span class="lp-autocomplete-type-badge">${badgeLabel}</span>
              </div>
              <p class="lp-autocomplete-meta">${highlightMatch(row.extra || '', query)}</p>
              <p class="lp-autocomplete-reason">${reasonText}</p>
            </div>
            <span class="lp-autocomplete-cta">${escapeHtml(ctaText)}</span>
          </div>
        </a>
      `;
    }

    function wrapDropdown(innerHtml) {
      const navOffsetClasses = inNavBar ? ' lp-autocomplete-dropdown-navbar' : '';
      return `
        <div class="lp-autocomplete-dropdown${navOffsetClasses}">
          ${innerHtml}
        </div>
      `;
    }

    function renderResults(html) {
      resultsContainer.innerHTML = html;
      syncDropdownViewport();
    }

    function syncDropdownViewport() {
      const dropdown = resultsContainer.querySelector('.lp-autocomplete-dropdown');
      if (!dropdown) return;

      if (window.innerWidth >= MOBILE_BREAKPOINT) {
        dropdown.classList.remove('is-mobile-viewport');
        dropdown.style.position = '';
        dropdown.style.top = '';
        dropdown.style.left = '';
        dropdown.style.right = '';
        dropdown.style.width = '';
        dropdown.style.maxHeight = '';
        return;
      }

      const visualViewport = window.visualViewport;
      const viewportWidth = visualViewport ? visualViewport.width : window.innerWidth;
      const viewportHeight = visualViewport ? visualViewport.height : window.innerHeight;
      const viewportOffsetTop = visualViewport ? visualViewport.offsetTop : 0;
      const viewportOffsetLeft = visualViewport ? visualViewport.offsetLeft : 0;
      const viewportBottom = viewportOffsetTop + viewportHeight;
      const inputRect = inputElement.getBoundingClientRect();
      const gutter = 12;
      const minHeight = 160;
      const width = Math.max(0, Math.floor(viewportWidth - (gutter * 2)));
      const targetTop = Math.floor(viewportOffsetTop + inputRect.bottom + 8);
      const fallbackTop = Math.floor(viewportBottom - minHeight - gutter);
      const top = Math.max(
        Math.floor(viewportOffsetTop + gutter),
        Math.min(targetTop, fallbackTop)
      );
      const availableHeight = Math.max(96, Math.floor(viewportBottom - top - gutter));
      const boundedHeight = Math.min(availableHeight, 420);

      dropdown.classList.add('is-mobile-viewport');
      dropdown.style.position = 'fixed';
      dropdown.style.top = `${top}px`;
      dropdown.style.left = `${Math.floor(viewportOffsetLeft + gutter)}px`;
      dropdown.style.right = 'auto';
      dropdown.style.width = `${width}px`;
      dropdown.style.maxHeight = `${boundedHeight}px`;
    }

    function highlightMatch(text, query) {
      const value = String(text || '');
      const token = String(query || '').trim();
      if (!token) return escapeHtml(value);

      const escapedToken = token.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      if (!escapedToken) return escapeHtml(value);

      const regex = new RegExp(`(${escapedToken})`, 'ig');
      return escapeHtml(value).replace(regex, '<mark class="lp-autocomplete-match">$1</mark>');
    }

    function clearAutocompleteResults() {
      resultsContainer.innerHTML = '';
    }

    function touchCacheKey(key) {
      const index = cacheOrder.indexOf(key);
      if (index > -1) cacheOrder.splice(index, 1);
      cacheOrder.push(key);
    }

    function pruneCache() {
      while (cacheOrder.length > MAX_CACHE_SIZE) {
        const oldest = cacheOrder.shift();
        delete resultsCache[oldest];
      }
    }

    function csrfToken() {
      const token = document.querySelector('meta[name="csrf-token"]');
      return token ? token.content : null;
    }

    function escapeHtml(value) {
      return String(value || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
    }

    function escapeHtmlAttr(value) {
      return escapeHtml(value).replace(/"/g, '&quot;');
    }

    function trackSearchClick({ filerId, source, query }) {
      if (!searchClickUrl || !filerId) return;

      const key = [filerId, source || 'unknown', (query || '').trim().toLowerCase()].join(':');
      const now = Date.now();
      if (recentTrackedClicks[key] && (now - recentTrackedClicks[key] < TRACK_DEDUPE_MS)) return;
      recentTrackedClicks[key] = now;

      if (typeof window.lpTrackGrowthEvent === 'function') {
        window.lpTrackGrowthEvent('lp_search_result_click', {
          module: 'search_results',
          cta_label: 'Open search result',
          search_source: source || 'autocomplete',
          entity_id: filerId,
          search_query_length: (query || '').trim().length
        });
      }

      const body = JSON.stringify({
        filer_id: filerId,
        source: source || 'autocomplete',
        q: (query || '').trim()
      });

      if (navigator.sendBeacon) {
        const blob = new Blob([body], { type: 'application/json' });
        const sent = navigator.sendBeacon(searchClickUrl, blob);
        if (sent) return;
      }

      const headers = { 'Content-Type': 'application/json' };
      const token = csrfToken();
      if (token) headers['X-CSRF-Token'] = token;

      fetch(searchClickUrl, {
        method: 'POST',
        headers: headers,
        body: body,
        credentials: 'same-origin',
        keepalive: true
      }).catch(() => {});
    }

    inputElement.clearAutocompleteCache = function () {
      resultsCache = {};
      cacheOrder = [];
    };
  }

  window.clearAutocompleteCache = function () {
    autocompleteRoots.forEach((root) => {
      const input = root.querySelector('[data-autocomplete-input]');
      if (input && typeof input.clearAutocompleteCache === 'function') input.clearAutocompleteCache();
    });
  };
});
